import { Task } from '@sketchnote/gantt-chart';
import { AttachmentIcon, EyeIcon, UpvoteIcon } from '@sketchnote/icons';
import { Avatar, AvatarGroup, renderPriorities } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { calculatePercentage } from 'src/features/kanban/component/Item';
import { getFormattedValue } from 'src/utils/getFormattedValue';

export const TaskTooltip = ({ task }: { task: Task; fontSize: string; fontFamily: string }) => {
  return (
    <div className="w-full relative shadow-mdl4 rounded-md cursor-pointer text-left mt-16 bg-white min-w-284">
      <div className="p-12">
        {task.data?.label && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${
                task.data.label.length > 6 ? 6 : task.data.label.length
              }, minmax(0, 1fr))`,
            }}
            className="gap-x-4 gap-y-6 mb-10"
          >
            {task.data?.label.map((obj, index) => (
              <div
                key={index}
                style={{ backgroundColor: obj.color }}
                className="h-10 rounded-sm block w-full"
              ></div>
            ))}
          </div>
        )}
        <div className="flex mb-12">
          <div
            className={cn(
              'text-sm mt-2 flex font-medium',
              { 'text-gray-500 ': !task.data?.name },
              { 'ml-8': task.data?.type === 'creation' }
            )}
          >
            {getFormattedValue(task.data?.name) || 'Untitled'}
          </div>
        </div>
        {task.data?.subtaskCount !== undefined && task.data?.subtaskCount > 0 && (
          <div className="relative overflow-hidden mb-14">
            <Tippy
              content={`${calculatePercentage(
                task.data?.subtaskCount,
                task.data?.completedSubTaskTotal || 0
              )}%`}
            >
              <ProgressBar
                now={calculatePercentage(
                  task.data?.subtaskCount,
                  task.data?.completedSubTaskTotal || 0
                )}
                variant="success"
                className="board-item-progress"
              />
            </Tippy>
            <span className="text-xs text-gray-400">
              {task.data?.completedSubTaskTotal || 0} of {task.data?.subtaskCount || 0}
            </span>
          </div>
        )}

        <div className="flex  justify-between items-center mt-32">
          <div className="flex items-center">
            {task.data?.assignee?.length ? (
              <div className="flex">
                <AvatarGroup maxCount={1}>
                  {task.data?.assignee.map((obj) => (
                    <Avatar
                      name={cn(obj.firstname, obj.lastname)}
                      src={obj.avatar}
                      type="photo"
                      key={obj.memberId}
                      wrapperClassName="-mr-8 border-2 border-white"
                    />
                  ))}
                </AvatarGroup>
                {task.data?.assignee.length > 2 && (
                  <Avatar
                    name={task.data?.assignee.length - 2}
                    tooltip={false}
                    type={'initials'}
                    src={''}
                    wrapperClassName="-ml-10 border-white border-1 transform transition duration-500 hover:scale-125"
                  />
                )}
              </div>
            ) : null}
          </div>
          <span>{task.data?.priority && renderPriorities[task.data?.priority]}</span>
        </div>
      </div>
      <div className="flex items-center justify-between px-12 py-6">
        <div className="flex items-center text-xs">
          {task.data?.watch && <EyeIcon className="text-gray-500 mr-2 mt-2 text-sm" />}

          {task.data?.votes ? (
            <button
              className={cn(
                'p-5 hover:bg-gray-100 active:text-gray-600 flex items-center text-gray-500'
              )}
            >
              <UpvoteIcon /> <span className="ml-5">{task.data?.votes}</span>
            </button>
          ) : null}
          {task.data?.attachments && task.data?.attachments?.length > 0 && (
            <div className="flex items-center px-2 text-sm">
              <AttachmentIcon className="text-gray-500 mr-2 " />
              <span className="leading-none text-gray-500">{task.data?.attachments?.length}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
